import { IMobileInventoryCountDetailDto } from '../models/IMobileInventoryCountDetailDto';

/**
 * Selects items that belong on the mobile **Pull** view for an active count.
 *
 * The previous implementation required `short > 0 || pulled > 0`, which excluded
 * every item on a freshly-started Mobilize - Pull/Return count (where the user
 * skipped the initial-count step, so short/pulled are still null). That left the
 * mobile Pull screen showing "No tool groups" while the web showed the full
 * template — see the iPad screenshot tied to this fix.
 *
 * The corrected rule keeps the in-progress + shortage cases the legacy logic
 * already handled, and additionally includes fresh items the user has not
 * counted yet so they can start pulling immediately. Items with a zero
 * recommended level, or whose initial count exactly meets the recommended
 * level, stay hidden because there is nothing to pull.
 */
export function filterPullableItems(
  items: IMobileInventoryCountDetailDto[]
): IMobileInventoryCountDetailDto[] {
  return items.filter((item) => {
    const recommended = item.recommendedInventoryLevel ?? 0;
    if (recommended <= 0) return false;

    if ((item.pulled ?? 0) > 0) return true;
    if ((item.short ?? 0) > 0) return true;

    // Fresh row — no initial count yet, so we cannot rule out a shortage.
    // Surface it so the user can begin the pull without backtracking to the
    // initial-count step.
    return item.initialCount == null;
  });
}

/**
 * Selects items that belong on the mobile **Return** view for an active count.
 *
 * Return mode stays restrictive: a user can only return excess that has been
 * confirmed by an initial count (`over > 0`) or that they have already begun
 * returning (`returned > 0`). Fresh, uncounted items are intentionally
 * excluded — the user has no basis for returning items they have not yet
 * verified are over-stocked.
 */
export function filterReturnableItems(
  items: IMobileInventoryCountDetailDto[]
): IMobileInventoryCountDetailDto[] {
  return items.filter(
    (item) => (item.returned ?? 0) > 0 || (item.over ?? 0) > 0
  );
}
